import { ActivatedRouteSnapshot, ResolveFn, Route, RouterStateSnapshot, Routes } from '@angular/router';
import { AppAuthGuard } from './guards/app.auth-guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { SaGuard } from './sa/sa.guard';
import { OfferingResolved, OfferingService } from './services/offerings.service';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isGenericHttpError } from '@lib-common/services/http.service';
import { CompanyHeader } from './types/store.types';
import { CompanyService } from './services/company.service';
import { environment } from '../environments/environments';
import { categoryHubResolver } from './category-hub/category-hub.resolver';
import { legacyCategoryRedirectGuard } from './guards/legacy-category-redirect.guard';
import { defaultCountryRedirectGuard } from './guards/default-country-redirect.guard';
import { searchRouteGuard } from './guards/search-route.guard';
import { canMatchActiveOpportunities } from './company/components/sidebar/active-opportunities/active-opportunities.feature';
import { canMatchCompanyPreview, canMatchProfileOpportunities } from './company/profile/page/company-profile-desktop';
import { PHONE_CHROME_DATA } from './company/profile/mobile/company-profile-phone-chrome.service';
import { findJoinDoor, JOIN_DOOR, JoinDoorSlug } from './join/join-doors';
import { analyticsBoundaryGuard } from './consent/analytics-boundary.guard';

// Dev-only visual harness (Story 67.2a unified small card + 67.4 medium geometry
// + 67.5 wide geometry). Stripped in prod builds. The old offering-card gallery
// retired with the legacy card (67.2b-a).
// Story 50.6: every `dev/` route carries `analyticsBoundaryGuard`, like the token and SA routes below.
const devRoutes: Route[] = environment.production
  ? []
  : [
      {
        // Story 59.13 — unlisted AI-search showcase (chips, ranking badges,
        // suggest, context bonus, entity deep links). URL-only access.
        path: 'dev/ai-search-lab',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () => import('./_dev/ai-search-lab.component').then((m) => m.AiSearchLabComponent),
      },
      {
        path: 'dev/offering-small-card-gallery',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () =>
          import('./_dev/offering-small-card-gallery.component').then((m) => m.OfferingSmallCardGalleryComponent),
      },
      {
        path: 'dev/offering-medium-card-gallery',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () =>
          import('./_dev/offering-medium-card-gallery.component').then((m) => m.OfferingMediumCardGalleryComponent),
      },
      {
        path: 'dev/offering-wide-card-gallery',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () =>
          import('./_dev/offering-wide-card-gallery.component').then((m) => m.OfferingWideCardGalleryComponent),
      },
      {
        path: 'dev/upload-step-spike',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () =>
          import('./_dev/upload-step-spike.component').then((m) => m.UploadStepSpikeComponent),
      },
      {
        // Story 67.7b — render-only harness for the re-hosted edit-images +
        // add-variant CDK dialogs (mock data, no auth).
        path: 'dev/edit-variant-dialogs',
        canActivate: [analyticsBoundaryGuard],
        loadComponent: () =>
          import('./_dev/edit-variant-dialogs-harness.component').then(
            (m) => m.EditVariantDialogsHarnessComponent,
          ),
      },
    ];

const companyIdResolver: ResolveFn<number | null> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const companyId = route.paramMap.get('company_id');
  if (companyId === null) {
    throw new Error('Missing required route parameter: company_id');
  }
  const n = Number(companyId);
  // Guard: some list/search cards produce `/company/undefined/offerings/...` links
  // when a product card lacks `company_id`. Number("undefined") === NaN — we skip
  // the NaN downstream rather than propagating it into API calls.
  return Number.isFinite(n) ? n : null;
};

const companyResolver: ResolveFn<CompanyHeader | null> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const companyId = route.paramMap.get('company_id');
  if (companyId === null) {
    throw new Error('Missing required route parameter: company_id');
  }
  const n = Number(companyId);
  // Skip the GET when the URL carries a non-numeric company_id (e.g. "undefined"
  // from a link that wasn't fully hydrated). Prevents a pointless 422 and lets
  // the page render from the product-resolver's embedded company data instead.
  if (!Number.isFinite(n)) return of(null);
  // `CompanyService.getCompanyHeader` is typed Observable<any> internally (its
  // http.get call is untyped and its tap handler reads off-header fields like
  // `company.error`). Downstream route consumers treat the payload as a
  // CompanyHeader, so we surface that shape at the resolver boundary.
  //
  // ⚠⚠ AND WE NORMALISE THE ERROR VALUE TO null (Codex follow-up HIGH).
  // `HttpService` swallows HTTP errors into a TRUTHY `GenericHttpError` value
  // rather than throwing, and the old cast passed that straight through as a
  // `CompanyHeader`. The offering page then took the success branch with a
  // company whose every field — `company_id`, `name` — is undefined: it
  // answered 200 and built title, Open Graph and JSON-LD out of nothing. That
  // was invisible client-side and is indexable under SSR.
  //
  // ⚠ Known remaining imprecision, recorded rather than half-fixed: this
  // collapses a company 404 and a company 5xx to the same `null`, so the page
  // answers 404 for both. The offering-side distinction (404 vs 503) IS
  // carried, via `OfferingResolved`. Doing the same for the company means
  // widening the `company` input and rewrapping ~26 spec call sites, and a
  // mechanical rewrap is where assertions quietly stop asserting — filed as a
  // follow-up in `epic-78-notes` §D instead. The malformed-200, which is the
  // severe half, is gone.
  return inject(CompanyService)
    .getCompanyHeader(n)
    .pipe(
      map((company) =>
        isGenericHttpError(company) ? null : (company as CompanyHeader),
      ),
    ) as Observable<CompanyHeader | null>;
};

// ⚠ Story 78.2 — this route is server-rendered, so the resolver must
// distinguish "no such offering" from "the lookup failed": the page turns the
// former into a cacheable 404 and the latter into 503 + no-store. The
// in-component refetches keep using `getOfferingDetails`, which only needs the
// payload.
const productResolver: ResolveFn<OfferingResolved> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const productId = route.paramMap.get('offering_id');
  if (productId === null) {
    throw new Error('Missing required route parameter: offering_id');
  }
  return inject(OfferingService).getOfferingDetailsResolved(productId);
};

const JOIN_DOOR_TITLES = {
  [JOIN_DOOR.company.slug]: 'Company submission · Unita',
  [JOIN_DOOR.influencer.slug]: 'Influencer submission · Unita',
  [JOIN_DOOR.artist.slug]: 'Artist submission · Unita',
  [JOIN_DOOR.organization.slug]: 'Organization submission · Unita',
  [JOIN_DOOR['link-help'].slug]: 'Link help · Unita',
  [JOIN_DOOR.personal.slug]: 'Personal accounts · Unita',
} as const satisfies Readonly<Record<JoinDoorSlug, string>>;

/** Browser title for the shell-less joining-funnel door route. */
export const joinDoorTitleResolver: ResolveFn<string> = (route) => {
  const door = findJoinDoor(route.paramMap.get('door'));
  return door === undefined ? 'Join Unita · Unita' : JOIN_DOOR_TITLES[door.slug];
};

export const routes: Routes = [
  // OIDC callback — MUST be a top-level sibling (NOT a child of `authentication`)
  // The `authentication` parent has `userBlackListed: true` which would redirect
  // authenticated users to `/`, blocking the callback. This route must be processable
  // by users who already have a session (e.g. token refresh redirect).
  {
    path: 'authentication/callback',
    loadComponent: () => import('./auth/callback.component').then((m) => m.CallbackComponent),
  },
  // Auth routes — no app shell
  {
    path: 'authentication',
    canActivate: [AppAuthGuard],
    data: { userBlackListed: true },
    children: [
      {
        path: 'login',
        loadComponent: () => import('./auth/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'register',
        redirectTo: '/join',
        pathMatch: 'full',
      },
    ],
  },
  // Story 50.6 — the four token routes carry the token in the PATH, so the Contentsquare tag must
  // never share a document with them: `analyticsBoundaryGuard` turns an in-app navigation into one
  // into a full page load when the tag is present (consent/analytics-boundary.ts).
  // Onboarding form — no app shell (standalone layout with sidebar + world map)
  {
    path: 'apply/:token',
    canActivate: [analyticsBoundaryGuard],
    loadComponent: () => import('./onboarding/onboarding-shell.component').then((m) => m.OnboardingShellComponent),
  },
  // Application status page — no app shell (standalone page, public via token)
  {
    path: 'status/:token',
    canActivate: [analyticsBoundaryGuard],
    loadComponent: () =>
      import('./onboarding/status/onboarding-status.component').then((m) => m.OnboardingStatusComponent),
  },
  // Ownership transfer — no app shell (standalone public page via token)
  {
    path: 'transfer/:token',
    canActivate: [analyticsBoundaryGuard],
    loadComponent: () =>
      import('./onboarding/transfer/transfer-accept.component').then((m) => m.TransferAcceptComponent),
  },
  // Resubmission form — same shell component in resubmission mode
  {
    path: 'resubmit/:token',
    canActivate: [analyticsBoundaryGuard],
    loadComponent: () => import('./onboarding/onboarding-shell.component').then((m) => m.OnboardingShellComponent),
    data: { isResubmission: true },
  },
  // Epic 76 Story 76.3a — intake screens own their full viewport and therefore
  // sit outside the persistent app shell. Keep this before the shell route.
  {
    path: 'join/:door',
    title: joinDoorTitleResolver,
    loadComponent: () => import('./join/funnel/join-door.component').then((m) => m.JoinDoorComponent),
  },
  // All non-auth routes wrapped in AppShellComponent (persistent nav/footer chrome)
  {
    path: '',
    loadComponent: () => import('./app-shell/app-shell.component').then((m) => m.AppShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./front-page/home-page.component').then((m) => m.HomePageComponent),
      },
      {
        // Story 36.5b-2 — the search page (Epic 36), at /search since Story 36.10b (ruling 47), which retired the legacy page's
        // route. Its guard turns a legacy link's #companies|#offerings|#all into `type`, once.
        // Story 36.7b (D27): its own box makes the nav's redundant — the shell hides both nav rows here.
        path: 'search',
        title: 'Search · Unita',
        data: { hideNavSearch: true },
        canActivate: [searchRouteGuard],
        loadComponent: () => import('./search/search-page.component').then((m) => m.SearchPageComponent),
      },
      {
        // Story 36.10b — the page's address until 36.10b, kept for the links in the wild: guard-only, it redirects to /search with
        // every query value. The server-side 301 is Epic 55's.
        path: 'ai-search',
        pathMatch: 'full',
        canActivate: [searchRouteGuard],
        children: [],
      },
      {
        // Epic 68 M.7b — messaging inbox (authenticated-only). Feature-scoped InboxStore is
        // provided inside InboxPageComponent; the realtime connection opens/closes with the route.
        path: 'inbox',
        canActivate: [AppAuthGuard],
        data: { authenticated: true },
        loadComponent: () => import('./messaging/inbox-page.component').then((m) => m.InboxPageComponent),
      },
      {
        // Epic 68 M.4b — message requests live INSIDE the inbox (Chats/Archives tabs,
        // decision E). The old standalone route stays as a redirect for bookmarks.
        path: 'requests',
        redirectTo: 'inbox',
        pathMatch: 'full',
      },
      {
        // Epic 87 Story 87.16 (R21): the server's own route for the company family's link previews. It matches only
        // during a server render, where `companyProfileDesktop` is on, of `/company/:id` and its listed leaves
        // (`canMatchCompanyPreview`), and loads nothing but the preview component, which writes the head and draws
        // nothing; the browser never matches it, so the family below serves the page exactly as before.
        path: 'company/:company_id',
        canMatch: [canMatchCompanyPreview],
        children: [
          {
            path: '**',
            // 87.21 (R24): the phone chrome rule's mark, so the server render hides the site's chrome below 1280
            data: PHONE_CHROME_DATA,
            loadComponent: () =>
              import('./company/profile/page/company-preview.component').then((m) => m.CompanyPreviewComponent),
          },
        ],
      },
      {
        path: 'company/:company_id',
        children: [
          {
            path: '',
            // Epic 87 Story 87.3a: the gate renders the desktop profile host at ≥ 1280 px (flag
            // `companyProfileDesktop`, leaves '' / home / about / docs / contact) and the tabbed
            // CompanyComponent everywhere else; the children below stay the old page's tabs.
            loadComponent: () =>
              import('./company/profile/page/company-profile-gate.component').then((m) => m.CompanyProfileGateComponent),
            children: [
              {
                // Default tab — landing on /company/:company_id with no tab
                // segment resolves to the Overview tab so the profile renders
                // immediately instead of an empty shell.
                path: '',
                redirectTo: 'home',
                pathMatch: 'full',
              },
              {
                path: 'home',
                // 87.21 (R24): a leaf the phone host serves, so the shell hides its chrome below 1280 from the first paint
                data: PHONE_CHROME_DATA,
                loadComponent: () =>
                  import('./company/components/company-layout.component').then((m) => m.CompanyLayoutComponent),
              },
              {
                // Old URL — redirect for bookmarks / external links. Component
                // file + class name still use "Products" per the deferred
                // incremental-rename strategy (see plan item #5 — rename the
                // URL only; class/file rename punted to the cleanup epic).
                path: 'products',
                redirectTo: 'offerings',
                pathMatch: 'full',
              },
              {
                path: 'offerings',
                loadComponent: () =>
                  import('./company/components/products/products.component').then((m) => m.ProductsComponent),
              },
              {
                path: 'docs',
                data: PHONE_CHROME_DATA,
                loadComponent: () =>
                  import('./company/components/documents-and-certifications.component').then(
                    (m) => m.DocumentsAndCertificationsComponent,
                  ),
              },
              {
                path: 'about',
                data: PHONE_CHROME_DATA,
                loadComponent: () => import('./company/components/about.component').then((m) => m.AboutComponent),
              },
              {
                path: 'contact',
                data: PHONE_CHROME_DATA,
                loadComponent: () => import('./company/components/contact.component').then((m) => m.ContactComponent),
              },
              {
                // Epic 31 Story 31.9a — Active Opportunities View-all page.
                // Child of CompanyComponent so the company header + nav chrome
                // stay. Feature-flagged (AC 9): flag off ⇒ canMatch fails and
                // the URL falls through to the `**` redirect.
                // Epic 87 Story 87.6b (D19, C-12): at 1280 px and wider with
                // `companyProfileDesktop` on, the same URL is the desktop page's
                // Wants and Offers section (the gate renders it), whatever
                // `activeOpportunities` says; below 1280 the old list serves
                // while that flag is on.
                path: 'opportunities',
                canMatch: [canMatchProfileOpportunities],
                data: PHONE_CHROME_DATA,
                loadComponent: () =>
                  import(
                    './company/components/sidebar/active-opportunities/active-opportunities-page.component'
                  ).then((m) => m.ActiveOpportunitiesPageComponent),
              },
              {
                // 87.6b (C-12; T0 M6): `/supply` keeps the old page's public
                // supply panel at every width until a new supply section
                // exists. It lands here, an unmapped leaf the gate serves on
                // the old page; the old page reads the path as its
                // Opportunities tab (`startsWith('opportunities')`).
                path: 'opportunities/supply',
                canMatch: [canMatchActiveOpportunities],
                loadComponent: () =>
                  import(
                    './company/components/sidebar/active-opportunities/active-opportunities-page.component'
                  ).then((m) => m.ActiveOpportunitiesPageComponent),
              },
              {
                // 77.10 ruling #1 — ONE tab. The Supply routes are RETIRED
                // after rehoming (AC3): the public projection renders inside
                // the Opportunities tab, the owner/demand panes inside
                // Opportunities → Manage. Angular redirects, never server
                // rewrites (/supply/requirements is also a backend API
                // prefix). Neither retired page read a query param or
                // fragment, so there are no deep links to preserve. Mobile
                // owners lose nothing (the OD2=A ruling): the embedded panes
                // keep the pages' own API-authoritative posture, with no
                // client capability predicate and no isMobile clause.
                path: 'supply',
                pathMatch: 'full',
                redirectTo: 'opportunities/supply',
              },
              {
                path: 'supply/manage',
                redirectTo: 'opportunities/manage',
              },
              {
                path: 'supply/requirements',
                redirectTo: 'opportunities/manage',
              },
              {
                // Epic 31 Story 31.9b — owner Manage-all page (drafts visible
                // here ONLY). Same canMatch flag gate. Authorization is the
                // API's: a visitor deep-linking here gets the manage
                // endpoint's 401/403 error panel, never draft data.
                path: 'opportunities/manage',
                canMatch: [canMatchActiveOpportunities],
                loadComponent: () =>
                  import(
                    './company/components/sidebar/active-opportunities/active-opportunities-manage-page.component'
                  ).then((m) => m.ActiveOpportunitiesManagePageComponent),
              },
            ],
          },
          {
            // Not a child of CompanyComponent as it doesn't have the company header
            path: 'offerings/:offering_id',
            loadComponent: () =>
              import('./company/components/offerings/offering.page.component').then((m) => m.OfferingPageComponent),
            resolve: {
              companyId: companyIdResolver,
              product: productResolver,
              company: companyResolver,
            },
          },
        ],
      },
      {
        // Story 30.10b — the All-Categories index page (the catalog front door).
        // Bare `/categories` renders the new uniform category grid; the 30.10
        // `legacyCategoryRedirectGuard` STAYS so `/categories?id=X` still
        // redirects to the Category-Hub (`/c/:slug`, 30.4). The legacy
        // `CategoryResultsComponent` is now UNREFERENCED (the Hub `/c/:slug`
        // loads `CategoryHubComponent`, not it) — safe to delete in the
        // deferred 30.10 cleanup; the guard stays for `/categories?id=...`.
        path: 'categories',
        canActivate: [legacyCategoryRedirectGuard],
        loadComponent: () =>
          import('./home/components/all-categories-index.component').then((m) => m.AllCategoriesIndexComponent),
      },
      {
        // Epic 30 Story 30.4 — Category-Hub container. The route key is the
        // SLUG (30.1 lock — supersedes architecture D11's `:categoryId`). A
        // resolver runs the compose fetch server-side so SSR/prerender HTML ==
        // first client render (AC7). `/categories` (the legacy listing) is left
        // untouched — its repurpose + old-URL redirects are Story 30.10's scope.
        path: 'c/:slug',
        loadComponent: () => import('./category-hub/category-hub.component').then((m) => m.CategoryHubComponent),
        resolve: { hub: categoryHubResolver },
      },
      {
        // Story 34.4 AC5 (Codex M1): dynamic default — redirect to the first
        // alphabetical PUBLISHED country, not a hardcoded `al`. See the guard.
        path: 'country',
        pathMatch: 'full',
        canActivate: [defaultCountryRedirectGuard],
        children: [],
      },
      {
        // Story 34.25 — the per-country news index, the exit 34.21's hub section
        // did not have.
        //
        // ⚠ A SIBLING of the hub, not a child: a child route keeps the 389-line
        // `CountryComponent` (hub KPIs, trade agreements, snapshot, companies)
        // mounted to render a page that needs only the country's identity.
        //
        // ⚠ Declared BEFORE `country/:country_code`. Angular takes the first
        // route whose path CONSUMES the URL, and a `loadComponent` route with no
        // children only matches when nothing is left over — so the order is not
        // load-bearing today. It is declared first anyway because the day this
        // hub grows `children`, the parametric route above would start swallowing
        // `country/al/news` silently, and the fix would be this line's position.
        path: 'country/:country_code/news',
        loadComponent: () =>
          import('./country/country-news-index.component').then((m) => m.CountryNewsIndexComponent),
      },
      {
        path: 'country/:country_code',
        loadComponent: () => import('./country/country.component').then((m) => m.CountryComponent),
      },
      // Placeholder routes for nav items that don't have pages yet (Epic 10 will replace)
      // NOTE: `/browse` was DROPPED entirely in Story 9.4 (PO 2026-04-22) —
      // `/` is the canonical front-page route; the wildcard below catches
      // stray `/browse` visits and redirects to `/`.
      {
        // Epic 74 Story 74.2 — the Companies Feed replaces the placeholder.
        // Public and non-personalized (FR24), so no guard. `/ngos` below is
        // still a placeholder and stays one.
        path: 'companies',
        loadComponent: () =>
          import('./companies-feed/companies-feed-page.component').then(
            (m) => m.CompaniesFeedPageComponent,
          ),
      },
      {
        // Epic 76 Stories 76.2/76.4 — the public joining funnel's front door,
        // INSIDE the shell (PO ruling Q3, 2026-08-24). It is linked from the
        // public shell and Browse surfaces, remains indexable, and owns its
        // canonical metadata in `join-entry-page.component.ts`.
        path: 'join',
        title: 'Become part of Unita · Unita',
        loadComponent: () =>
          import('./join/join-entry-page.component').then((m) => m.JoinEntryPageComponent),
      },
      // Angular 21's embedded wildcard matches zero or more segments. Keep the
      // exact chooser first so bare `/join` does not self-redirect, then catch
      // malformed nested door URLs before the shell's global wildcard.
      { path: 'join/**', redirectTo: '/join', pathMatch: 'full' },
      {
        // 76.2/76.4 — compatibility alias for external `/get-listed` links.
        // Category hubs now link canonically to `/join/company?category=...`;
        // this RELATIVE `redirectTo` keeps legacy query strings intact
        // (asserted in `join/join-routes.spec.ts`, not assumed).
        path: 'get-listed',
        redirectTo: 'join',
        pathMatch: 'full',
      },
      {
        // Story 34.23 — the article reader DD4 turned country-hub headlines
        // into. `:ref` is `{id}-{slug}`; it resolves on the ID ALONE, so a
        // stale or absent slug still serves and the page canonicalises the
        // address bar afterwards. That is why 34.22a's slug carries no unique
        // constraint — do not "tighten" this to a slug lookup.
        //
        // Unknown ids render the component's own not-found state: the app has
        // no 404 route and its catch-all (below) silently redirects to the
        // homepage, which for a stale shared link reads as Unita bouncing the
        // visitor to the front page.
        path: 'news/:ref',
        loadComponent: () =>
          import('./news/article-page.component').then(
            (m) => m.ArticlePageComponent,
          ),
      },
      {
        // Epic 74 Story 74.3 — the editorial story page for one feed entry.
        // Public, no guard (FR24). Resolves by slug for any published issue;
        // unknown/draft/non-public slugs render the in-component not-found
        // state (the app has no 404 route — D7).
        path: 'companies/story/:slug',
        loadComponent: () =>
          import('./companies-feed/story/story-page.component').then(
            (m) => m.StoryPageComponent,
          ),
      },
      {
        path: 'ngos',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      {
        /**
         * Epic 82 Story 82.11a — first-time creator setup. Auth-gated.
         *
         * ⚠ BEFORE `influencers/:slug`, like every other literal segment here, or the slug pattern swallows
         * it and a creator lands on a profile page for a creator called "setup".
         *
         * It consumes a profile that join approval or an admin already provisioned; it never creates one.
         */
        path: 'influencers/setup',
        canActivate: [AppAuthGuard],
        data: { authenticated: true },
        // 82.11L (T0 F8): setup draws its own frame, so the document title is its only name outside it
        title: 'Set up your profile · Unita',
        loadComponent: () =>
          import('./influencer/profile-v2/setup/setup-page.component').then((m) => m.SetupPageComponent),
      },
      {
        // Story 79.8b-1 (AC 2): the retired self-serve wizard's address goes to the Epic 76 join form — a creator
        // joins there now (R7). Declared BEFORE `influencers/:slug`: unredirected, the slug route would render the
        // v2 profile for a creator called "apply".
        path: 'influencers/apply',
        pathMatch: 'full',
        redirectTo: '/join/influencer',
      },
      {
        // Story 79.8b-1 (AC 2, audit H1): the retired owner console's address goes to setup, which routes every
        // creator by their entry (setup, pending, completed → their page). Unredirected, the slug route would ask
        // the public read for a creator called "me" (recon FE risk 1).
        path: 'influencers/me',
        pathMatch: 'full',
        redirectTo: '/influencers/setup',
      },
      {
        // Epic 38 Story 38.6 — public influencer directory listing.
        // Unauthenticated. Declared AFTER the static /apply + /me paths and
        // BEFORE /influencers/:slug. The bare path and the :slug path are
        // distinct templates (so order is non-load-bearing here), but
        // bare-before-param matches the established convention.
        // Epic 88 Story 88.2 — the Influencers hub page frames the directory (embedded, untouched, until 88.3).
        path: 'influencers',
        loadComponent: () =>
          import('./influencer/hub/influencer-hub-page.component').then((m) => m.InfluencerHubPageComponent),
      },
      {
        // Epic 38 Story 38.4 — public profile page. Unauthenticated.
        // Declared AFTER the static /apply + /me paths so those win
        // route precedence in declaration order.
        // Story 79.3 — v2 owns the page store, layout mount and OG/SEO lifecycle.
        path: 'influencers/:slug',
        loadComponent: () =>
          import(
            './influencer/profile-v2/page/influencer-profile-v2-page.component'
          ).then((m) => m.InfluencerProfileV2PageComponent),
      },
      {
        // Epic 86 — the public Artists hub (it replaced the 72.9 directory
        // page). Unauthenticated. Declared BEFORE `artists/:slug` per the
        // literal-before-slug convention. NOTE: there is NO `/artists/me` FE
        // route by design — the owner views their pending profile at their
        // own `/artists/:slug` via the 72.3b fallback; the backend slug
        // generator's stop-list (me/apply/new/hub) keeps these literal
        // segments unmintable.
        path: 'artists',
        loadComponent: () =>
          import('./artist/hub/artist-hub-page.component').then(
            (m) => m.ArtistHubPageComponent,
          ),
      },
      {
        // Epic 72 Story 72.7 — artist onboarding apply page. Auth-gated.
        // MUST be declared BEFORE `artists/:slug`
        // so the literal `apply` segment isn't caught by the slug pattern
        // (the backend stop-list also keeps `apply` unmintable).
        path: 'artists/apply',
        canActivate: [AppAuthGuard],
        data: { authenticated: true },
        loadComponent: () =>
          import('./artist/apply/artist-apply.component').then((m) => m.ArtistApplyComponent),
      },
      {
        // Epic 72 Story 72.3a — public artist profile page. Unauthenticated.
        // Sibling of `influencers/:slug`; same undifferentiated-404 posture
        // (plus 422→not-found, F1).
        path: 'artists/:slug',
        loadComponent: () =>
          import('./artist/profile/artist-profile-page.component').then(
            (m) => m.ArtistProfilePageComponent,
          ),
      },
      // Epic 38 Story 38.3 — SA admin routes under `/sa/influencers`.
      // Minimal shell per Q2 lock 2026-05-15: no layout component, just
      // the two pages. Guard is auth-only today (`SaGuard` delegates to
      // the same session check as `AppAuthGuard`); backend is the
      // authoritative SA gate (all `/api/sa/*` endpoints 403 non-SA).
      // M2 review fix 2026-05-15 — using `SaGuard` (not `AppAuthGuard`)
      // gives a stable extension point: when the auth store learns about
      // atoms/roles in a future cross-cut, the SA role check lands inside
      // `SaGuard` without touching `app.routes.ts`.
      // Story 50.6 — every `sa/` route lists `analyticsBoundaryGuard` FIRST: staff pages show other
      // people's data, so the Contentsquare tag never shares a document with them.
      {
        path: 'sa/influencers',
        canActivate: [analyticsBoundaryGuard, SaGuard],
        loadComponent: () =>
          import('./sa/influencers/list/sa-influencer-list.component').then((m) => m.SaInfluencerListComponent),
      },
      {
        path: 'sa/influencers/:id',
        canActivate: [analyticsBoundaryGuard, SaGuard],
        loadComponent: () =>
          import('./sa/influencers/detail/sa-influencer-detail.component').then((m) => m.SaInfluencerDetailComponent),
      },
      // Story 74.7 — SA feed editor under `/sa/feed`. Same posture as
      // `/sa/influencers` above: `SaGuard` is auth-only, the backend 403 is
      // the authoritative SA gate, and per-domain pages with no shared SA
      // chrome (Q2 lock 2026-05-15). The editor page additionally blocks
      // dirty navigation via the repo's first `CanDeactivateFn` (AC12).
      {
        path: 'sa/feed',
        canActivate: [analyticsBoundaryGuard, SaGuard],
        loadComponent: () =>
          import('./sa/feed/sa-feed-list.component').then((m) => m.SaFeedListComponent),
      },
      {
        path: 'sa/feed/issues/:id',
        canActivate: [analyticsBoundaryGuard, SaGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./sa/feed/sa-feed-editor-page.component').then((m) => m.SaFeedEditorPageComponent),
      },
      {
        path: 'about',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      {
        path: 'coming-soon',
        title: 'Coming soon · Unita',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      {
        path: 'recommender',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      // Footer-linked routes. The four legal pages below are REAL (pages/legal/,
      // built 2026-07-14 as a Story 38.26 filing prerequisite); pricing/newsroom
      // remain placeholders.
      {
        path: 'pricing',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      {
        path: 'newsroom',
        loadComponent: () => import('./pages/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
      },
      {
        path: 'privacy-policy',
        title: 'Privacy Policy · Unita',
        loadComponent: () => import('./pages/legal/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
      },
      {
        path: 'terms',
        title: 'Terms of Service · Unita',
        loadComponent: () => import('./pages/legal/terms-of-service.component').then((m) => m.TermsOfServiceComponent),
      },
      {
        path: 'cookie-policy',
        title: 'Cookie Policy · Unita',
        loadComponent: () => import('./pages/legal/cookie-policy.component').then((m) => m.CookiePolicyComponent),
      },
      {
        path: 'data-subject-rights',
        title: 'Data Subject Rights · Unita',
        loadComponent: () => import('./pages/legal/data-subject-rights.component').then((m) => m.DataSubjectRightsComponent),
      },
      ...devRoutes,
      { path: '**', redirectTo: '', pathMatch: 'full' },
    ],
  },
];
